<template>
  <div class="leaderboard-page">
    <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-trophy"></i>
        </div>
        <div>
          <h1 class="page-title">Leaderboard</h1>
          <p class="page-subtitle">Top performing loan officers by units and volume</p>
        </div>
      </div>
    </PageHeaderPortal>

    <div class="leaderboard-toolbar">
      <select v-model="period" class="leaderboard-period-select" @change="loadLeaderboards">
        <option value="today">Today</option>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
        <option value="ytd">YTD {{ currentYear }}</option>
      </select>
    </div>

    <div class="leaderboard-error" v-if="error">
      <i class="fa-solid fa-triangle-exclamation"></i> {{ error }}
      <button type="button" class="leaderboard-retry-btn" @click="loadLeaderboards">Retry</button>
    </div>

    <div class="leaderboard-grid">
      <!-- ── Units ── -->
      <div class="leaderboard-card">
        <div class="leaderboard-card-header">
          <div class="leaderboard-trophy leaderboard-trophy--units">
            <i class="fa-solid fa-trophy"></i>
          </div>
          <div class="leaderboard-card-heading">
            <h2>Units Leaderboard</h2>
            <p>Total funded loan units</p>
          </div>

          <!-- ── President's Club ── -->
          <!-- Two lines only, height-capped to match the trophy icon (see .president-club-widget)
               so this card's header row — and everything below it — lines up with the Volume
               card's, which has no widget of its own. -->
          <div class="president-club-widget" v-if="!presidentClubError && (presidentClubLoading || presidentClubTiers.length)">
            <div class="pc-row-top">
              <span class="pc-label">President's Club</span>
              <span class="pc-next-pill" v-if="!presidentClubLoading && nextTier">{{ unitsToNextTier }} to {{ nextTier.name }}</span>
              <span class="pc-next-pill pc-next-pill--maxed" v-else-if="!presidentClubLoading && presidentClubTiers.length">Top Tier</span>
            </div>
            <div class="pc-row-bottom">
              <span class="pc-units">{{ presidentClubLoading ? '—' : presidentClubUnits }} Units</span>
              <span class="pc-thresholds" v-if="presidentClubTiers.length">
                <template v-for="(tier, idx) in presidentClubTiers" :key="tier.name">
                  <span class="pc-tier" :class="'pc-tier--' + tier.name.toLowerCase()" >{{ tier.name }} {{ tier.units }}</span>
                  <span class="pc-dot" v-if="idx < presidentClubTiers.length - 1" :key="'dot-' + tier.name">&bull;</span>
                </template>
              </span>
            </div>
          </div>
        </div>

        <div class="leaderboard-list-head">
          <span class="col-rank">RANK</span>
          <span class="col-name">LOAN OFFICER</span>
          <span class="col-value">UNITS</span>
        </div>

        <div class="leaderboard-list-body">
          <div class="leaderboard-loading" v-if="loading">
            <i class="fa-duotone fa-spinner-third fa-spin"></i> Loading…
          </div>
          <div class="leaderboard-empty" v-else-if="!loading && leadersByUnits.length === 0">
            No funded units in this period yet.
          </div>
          <div class="leaderboard-row" v-for="leader in leadersByUnits" :key="'u-' + (leader.loid || leader.title)">
            <span class="col-rank">
              <span class="rank-badge">{{ leader.rankunits }}</span>
            </span>
            <span class="col-name">
              <img class="leader-avatar" :src="leader.profileimageurl || DEFAULT_AVATAR" :alt="leader.title" @error="onAvatarError" />
              <span class="leader-name-block">
                <span class="leader-name">{{ leader.title }}</span>
                <span class="leader-state">{{ fullStateName(leader.state) }}</span>
              </span>
            </span>
            <span class="col-value">{{ leader.units }}</span>
          </div>
        </div>
      </div>

      <!-- ── Volume ── -->
      <div class="leaderboard-card">
        <div class="leaderboard-card-header">
          <div class="leaderboard-trophy leaderboard-trophy--volume">
            <i class="fa-solid fa-trophy"></i>
          </div>
          <div class="leaderboard-card-heading">
            <h2>Volume Leaderboard</h2>
            <p>Total funded loan volume</p>
          </div>
        </div>

        <div class="leaderboard-list-head">
          <span class="col-rank">RANK</span>
          <span class="col-name">LOAN OFFICER</span>
          <span class="col-value">VOLUME</span>
        </div>

        <div class="leaderboard-list-body">
          <div class="leaderboard-loading" v-if="loading">
            <i class="fa-duotone fa-spinner-third fa-spin"></i> Loading…
          </div>
          <div class="leaderboard-empty" v-else-if="!loading && leadersByVolume.length === 0">
            No funded volume in this period yet.
          </div>
          <div class="leaderboard-row" v-for="leader in leadersByVolume" :key="'v-' + (leader.loid || leader.title)">
            <span class="col-rank">
              <span class="rank-badge">{{ leader.rankvolume }}</span>
            </span>
            <span class="col-name">
              <img class="leader-avatar" :src="leader.profileimageurl || DEFAULT_AVATAR" :alt="leader.title" @error="onAvatarError" />
              <span class="leader-name-block">
                <span class="leader-name">{{ leader.title }}</span>
                <span class="leader-state">{{ fullStateName(leader.state) }}</span>
              </span>
            </span>
            <span class="col-value">{{ formatCurrency(leader.volume) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="leaderboard-banner">
      <i class="fa-solid fa-chart-simple"></i>
      <div class="leaderboard-banner-text">
        <h3>Driven People. Real Results.</h3>
        <p>Celebrating the loan officers who make home possible every day.</p>
      </div>
    </div>
  </div>
</template>

<script>
import auth from "@/authConfig.js"
import apiService from "@/api/api.service.js"
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"

// Same gray-silhouette placeholder cgs2022's leaderboard uses for LOs with no photo on file
// (or whose S3 lookup 404s) — matched exactly rather than inventing a different fallback.
const DEFAULT_AVATAR = "data:image/svg+xml;utf8," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
  '<circle cx="20" cy="20" r="20" fill="#d9d9d9"/>' +
  '<circle cx="20" cy="16" r="7" fill="#ffffff"/>' +
  '<path d="M6 35c0-8 6-13 14-13s14 5 14 13" fill="#ffffff"/>' +
  '</svg>'
)

// The API sends the two-letter abbreviation (see the sample leader object: "state": "MI") —
// the design wants the full name, so that's spelled out here rather than asking the backend
// to change its response shape.
const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia", PR: "Puerto Rico"
}

export default {
  name: "LeaderboardView",
  components: { PageHeaderPortal },
  data() {
    return {
      token: null,
      azureId: "",
      period: "ytd",
      loading: true,
      error: null,
      leadersByUnits: [],
      leadersByVolume: [],
      DEFAULT_AVATAR,
      presidentClubLoading: true,
      presidentClubError: null,
      presidentClubGoals: null,
      presidentClubUnits: null
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    // Tiers are ordered by their own unit threshold rather than a hardcoded
    // Gold/Platinum/Diamond sequence, so this stays correct even if a given
    // year's configured thresholds don't fall in the "usual" order.
    presidentClubTiers() {
      if (!this.presidentClubGoals) return []
      const raw = [
        { name: "Gold", units: this.presidentClubGoals.goldunits },
        { name: "Platinum", units: this.presidentClubGoals.platinumunits },
        { name: "Diamond", units: this.presidentClubGoals.diamondunits }
      ]
      return raw
        .filter(t => t.units !== null && t.units !== undefined && t.units !== "")
        .sort((a, b) => a.units - b.units)
    },
    nextTier() {
      if (this.presidentClubUnits === null || this.presidentClubUnits === undefined) return null
      return this.presidentClubTiers.find(t => this.presidentClubUnits < t.units) || null
    },
    unitsToNextTier() {
      if (!this.nextTier) return 0
      return this.nextTier.units - this.presidentClubUnits
    }
  },
  methods: {
    // sdt/edt for the four period choices. "week"/"month" are calendar week/month, not a
    // trailing 7/30 days — matches how the existing date-range selects elsewhere in this app
    // (e.g. the swiftLock dashboards) read "This Week"/"This Month".
    dateRangeFor(period) {
      const now = new Date()
      const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const endOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

      if (period === "today") {
        return { sdt: startOfDay(now), edt: endOfDay(now) }
      }
      if (period === "week") {
        const day = now.getDay() // 0 = Sunday
        const start = new Date(now)
        start.setDate(now.getDate() - day)
        return { sdt: startOfDay(start), edt: endOfDay(now) }
      }
      if (period === "month") {
        return { sdt: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), edt: endOfDay(now) }
      }
      // ytd
      return { sdt: startOfDay(new Date(now.getFullYear(), 0, 1)), edt: endOfDay(now) }
    },

    onAvatarError(e) {
      e.target.src = DEFAULT_AVATAR
    },

    fullStateName(abbr) {
      if (!abbr) return ""
      return STATE_NAMES[String(abbr).toUpperCase().trim()] || abbr
    },

    formatCurrency(n) {
      const value = Number(n) || 0
      return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    },

    async loadLeaderboards() {
      this.loading = true
      this.error = null
      const { sdt, edt } = this.dateRangeFor(this.period)

      try {
        const res = await apiService.GetLeaderBoards(
          {
            sdt,
            edt,
            type: "All",
            purpose: "All",
            groupBy: "lo",
            azureid: this.azureId,
            leaderType: "Funded"
          },
          this.token?.accessToken
        )

        if (res?.data?.isSuccess === false) {
          throw new Error(res.data.message || "The leaderboard service returned an error.")
        }

        const data = res?.data?.data || res?.data || {}
        this.leadersByUnits = (data.leadersByUnits || []).filter(l => l.units > 0)
        this.leadersByVolume = (data.leadersByVolume || []).filter(l => l.volume > 0)
      } catch (err) {
        this.error = "Couldn't load the leaderboard. " + (err?.message || "")
        this.leadersByUnits = []
        this.leadersByVolume = []
      } finally {
        this.loading = false
      }
    },

    // President's Club is a full-year (calendar YTD) achievement, independent of the
    // Today/Week/Month/YTD toolbar above, which only scopes the two ranking cards.
    async loadPresidentClubGoals() {
      const year = new Date().getFullYear()
      try {
        const res = await apiService.GetPresidentClubAchievementsByYear(year, this.token?.accessToken)
        const list = res?.data?.data || res?.data || []
        this.presidentClubGoals = Array.isArray(list) ? (list[0] || null) : list
      } catch (err) {
        this.presidentClubGoals = null
        this.presidentClubError = this.presidentClubError || (err?.message || "Couldn't load President's Club goals.")
      }
    },

    async loadMyPresidentClubUnits() {
      const year = new Date().getFullYear()
      const sdt = new Date(year, 0, 1)
      const edt = new Date(year, 11, 31, 23, 59, 59, 999)

      try {
        const res = await apiService.GetLeaderBoards(
          {
            sdt,
            edt,
            type: "All",
            purpose: "All",
            groupBy: "lo",
            azureid: this.azureId,
            leaderType: "Funded"
          },
          this.token?.accessToken
        )

        const data = res?.data?.data || res?.data || {}
        const leaders = data.leadersByUnits || []

        // The leaderboard rows are keyed by LOID, not azureId, so the current user's row
        // has to be located via their own LOID (extensionAttribute1), which the API
        // returns alongside the rankings as myLoid.onPremisesExtensionAttributes.
        let myLoid = null
        const rawMyLoid = data.myLoid
        if (rawMyLoid) {
          let ext = rawMyLoid.onPremisesExtensionAttributes
          if (typeof ext === "string") {
            try { ext = JSON.parse(ext) } catch (e) { ext = null }
          }
          myLoid = ext?.extensionAttribute1 || null
        }

        const mine = myLoid ? leaders.find(l => l.loid === myLoid) : null
        this.presidentClubUnits = mine ? (mine.units || 0) : 0
      } catch (err) {
        this.presidentClubUnits = null
        this.presidentClubError = this.presidentClubError || (err?.message || "Couldn't load your President's Club progress.")
      }
    },

    async loadPresidentClub() {
      this.presidentClubLoading = true
      this.presidentClubError = null
      await Promise.all([this.loadPresidentClubGoals(), this.loadMyPresidentClubUnits()])
      this.presidentClubLoading = false
    }
  },
  async mounted() {
    this.token = await auth.acquireTokenResponse()
    this.azureId = localStorage.getItem("azure_id") || ""
    await Promise.all([this.loadLeaderboards(), this.loadPresidentClub()])
  }
}
</script>

<style scoped>
.leaderboard-page {
  height: 100%;
  min-height: 0;
  padding: 16px 20px 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;   /* the page itself never scrolls — only the lists inside each card do */
}

.leaderboard-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
  flex: 0 0 auto;
}

.leaderboard-period-select {
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--secondary-ltr, #e2e8f0);
  border-radius: 8px;
  background: #fff;
  color: var(--primary, #1f2a44);
  font-weight: 600;
  font-size: 1.3rem;
  cursor: pointer;
}

.leaderboard-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 1.3rem;
  flex: 0 0 auto;
}

.leaderboard-retry-btn {
  margin-left: auto;
  border: 1px solid #b91c1c;
  background: transparent;
  color: #b91c1c;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  font-weight: 600;
}

.leaderboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  flex: 1 1 auto;
  min-height: 0;
}

@media (max-width: 900px) {
  .leaderboard-grid { grid-template-columns: 1fr; }
}

.leaderboard-card {
  background: #fff;
  border: 1px solid var(--secondary-ltr, #e6e6eb);
  border-radius: 14px;
  padding: 22px 24px 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;   /* fills its grid cell so the list body below can flex to the real leftover space */
}

.leaderboard-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  flex: 0 0 auto;
  /* Lets the President's Club block drop to its own line on a narrow card instead of
     squeezing the card title — it stays right-aligned either way. */
  flex-wrap: wrap;
}

.leaderboard-trophy {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2.2rem;
}

.leaderboard-trophy--units { background: var(--accent, #198277); }
.leaderboard-trophy--volume { background: var(--primary, #0b1f3a); }

.leaderboard-card-heading { flex: 1 1 auto; min-width: 0; }
.leaderboard-card-heading h2 { margin: 0; font-size: 1.9rem; font-weight: 700; color: var(--primary, #1f2a44); }
.leaderboard-card-heading p { margin: 2px 0 0; font-size: 1.25rem; color: #7b8397; }

.leaderboard-viewall-btn {
  border: 0;
  background: transparent;
  color: var(--primary-mid, #126ad3);
  font-weight: 600;
  font-size: 1.25rem;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.leaderboard-list-head {
  display: grid;
  grid-template-columns: 64px 1fr 130px;
  padding: 10px 6px;
  border-bottom: 1px solid var(--secondary-ltr, #eef0f4);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #9aa3b2;
  flex: 0 0 auto;
}

/* Flex-fills whatever's actually left in the card instead of a fixed pixel guess — that's what
   guarantees the banner below never gets pushed off-screen or forces the whole page to scroll;
   only this list scrolls, and only once its own real available space runs out. */
.leaderboard-list-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.leaderboard-row {
  display: grid;
  grid-template-columns: 64px 1fr 130px;
  align-items: center;
  padding: 14px 6px;
  border-bottom: 1px solid var(--secondary-ltr, #f1f2f6);
}

.leaderboard-row:last-child { border-bottom: 0; }

.col-value { text-align: right; font-weight: 700; font-size: 1.5rem; color: var(--primary, #1f2a44); }
.col-name { display: flex; align-items: center; gap: 14px; min-width: 0; }

.leader-name-block { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; }
.leader-name {
  font-weight: 600;
  font-size: 1.5rem;
  color: var(--primary, #1f2a44);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.leader-state { font-size: 1.15rem; color: #8a96a8; }

/* Pale blue, matching the original mockup exactly — reusing --primary-mid-lt, already the
   app's own token for this shade, rather than inventing a new hex. Was a solid saturated blue
   with white text before; the mockup uses a much softer background with dark text. */
.rank-badge {
  /* Fixed size (not em-based) so the circle stays put while the number
     inside it shrinks — previously the circle scaled with font-size too,
     so a smaller number just made the whole badge smaller instead of
     giving the digits more room. Flex centering keeps it dead-center
     regardless of 1 vs 2 digits. */
  width: 3.7rem;
  height: 3.7rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
  background: var(--primary-mid-lt, #b9d6f9);
  color: var(--primary, #051d3a);
}

/* Bigger, and deliberately larger than .rank-badge above — the photo should read as the
   primary element in each row, not the number. */
.leader-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f0f0f0;
  flex-shrink: 0;
}

.leaderboard-loading, .leaderboard-empty {
  padding: 24px 4px;
  text-align: center;
  color: #9aa3b2;
  font-size: 1.3rem;
}

.leaderboard-banner {
  position: relative;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 14px;
  background: var(--primary-ltst, #eceff9);
  color: var(--primary, #1f2a44);
  flex: 0 0 auto;   /* stays put — the scrollable list bodies above are the only thing that grows */
}

.leaderboard-banner i { font-size: 2.2rem; color: var(--primary-mid, #126ad3); flex: 0 0 auto; }
.leaderboard-banner-text { flex: 1 1 auto; min-width: 0; }
.leaderboard-banner h3 { margin: 0; font-size: 1.6rem; }
.leaderboard-banner p { margin: 2px 0 0; font-size: 1.2rem; color: #5b6b85; }

/* Lives inside the Units card's header row: margin-left auto pushes the whole block
   to the card's right edge, while its own contents stay left-aligned against that
   block's left edge rather than centering under each other.
   Height is pinned to the trophy icon's own height (see .leaderboard-trophy) and
   clipped — not just "usually fits" — so this widget can never push the header row
   taller than the Volume card's matching row has no widget at all. Two lines,
   vertically centered in that fixed box. */
.president-club-widget {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  flex: 0 0 auto;
  height: 56px;
  overflow: hidden;
}

.pc-row-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.pc-label {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #98a1b0;
  white-space: nowrap;
}

.pc-row-bottom {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 3px;
  flex-wrap: nowrap;
}

.pc-units {
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1;
  color: var(--primary, #0b1f3a);
  white-space: nowrap;
}

.pc-next-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 6px;
  background: var(--primary-mid-lt, #dce8fa);
  color: var(--primary-mid, #1f5fd0);
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.2;
  white-space: nowrap;
}

.pc-next-pill--maxed {
  background: #e3f5e9;
  color: #237a3f;
}

.pc-thresholds {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: nowrap;
  gap: 8px;
  font-size: 1.05rem;
  white-space: nowrap;
}

/* Each tier's threshold reads in its own color, number included. */
.pc-tier { font-weight: 600; }
.pc-tier--gold { color: #c8961c; }
.pc-tier--diamond { color: #4189d8; }
.pc-tier--platinum { color: #8494ab; }
.pc-dot { color: #c3c9d4; }
</style>